import { Injectable } from "@angular/core";
import { FirebaseError } from "firebase/app";
import { ConnectionService, ConnectionState } from "ng-connection-service";
import { MonoTypeOperatorFunction, Observable, of, throwError } from "rxjs";
import { catchError, debounceTime, delay, map, mergeMap, retryWhen, scan, shareReplay } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { CommonVariable } from "../app.constants";
import { SwForceUpdateService } from '../services/sw-force-update.service';

@Injectable({ providedIn: 'root' })
export class CatchErrorService {
  errorCountMax = 4;
  checkIndex = -1;
  delayTime = 1500
  readonly isOnline$: Observable<boolean>;

  constructor(
    private connectionService: ConnectionService,
    private swForceUpdateService: SwForceUpdateService,
  ) {
    // Field initialisers run before constructor params are assigned and trip TS2729
    // under Jest (ts-jest), which blocks dependent specs from loading.
    this.isOnline$ = this.connectionService.monitor().pipe(
      map((newState: ConnectionState) => {
        return newState.hasNetworkConnection && newState.hasInternetAccess;
      }),
      debounceTime(300),
      shareReplay(1)
    );
  }

  /**
   * Operator to handle document not found or permission denied errors gracefully.
   * Returns null instead of throwing, preventing Sentry noise for expected cases.
   * Use this for optional documents that may not exist yet.
   */
  handleDocumentNotFoundOrPermissionDenied = catchError((error: unknown) => {
    if (error instanceof FirebaseError) {
      if (this.swForceUpdateService.isFirestorePermissionDenied(error)) {
        console.warn(
          'CatchErrorService: Firestore permission-denied detected, attempting SW force update'
        );
        this.swForceUpdateService.attemptForceUpdate();
        // Must return an ObservableInput — bare `return` yields undefined and triggers
        // TypeError: You provided an invalid object where a stream was expected (LOTTOSOCIAL-PWA-1M60)
        return of(null);
      }
      // Handle permission-denied errors gracefully (document may not exist or user doesn't have access)
      if (error.code === 'permission-denied') {
        console.warn('[CatchErrorService] Document permission denied (expected for non-existent docs):', error.message);
        return of(null);
      }

      // Handle document-not-found errors gracefully
      if (error.code === 'not-found' || error.message.includes('No document')) {
        console.warn('[CatchErrorService] Document not found (expected for new users):', error.message);
        return of(null);
      }
    }

    // For other errors, re-throw them so they can be handled elsewhere or reported to Sentry
    return throwError(() => error);
  });

  /**
   * Creates an observable that safely handles document reads for optional documents.
   * Returns null instead of throwing when document doesn't exist or permission denied.
   * This prevents Sentry noise for expected scenarios like new users without documents.
   */
  safeDocumentRead<T>(): (source: Observable<T>) => Observable<T | null> {
    return catchError((error: unknown) => {
      if (error instanceof FirebaseError) {
        const errorCode = error.code;
        const errorMessage = error.message.toLowerCase();
        
        // Permission denied - user may not have access to this document
        if (errorCode === 'permission-denied' || errorMessage.includes('permission')) {
          console.warn('[CatchErrorService] Permission denied for document read:', errorMessage);
          return of(null);
        }
        
        // Document not found - document hasn't been created yet
        if (errorCode === 'not-found' || errorMessage.includes('no document') || errorMessage.includes('not-found')) {
          console.warn('[CatchErrorService] Document not found:', errorMessage);
          return of(null);
        }
      }
      
      // Re-throw unexpected errors for Sentry reporting
      console.error('[CatchErrorService] Unexpected error in document read:', error);
      return throwError(() => error);
    });
  }

  /**
   * Safe operator for reading Firestore collections.
   * Returns an empty array instead of throwing on permission-denied errors.
   * This prevents RxJS stream type violations when collections are inaccessible.
   */
  safeCollectionRead<T>(): (source: Observable<T[]>) => Observable<T[]> {
    return catchError((error: unknown) => {
      if (error instanceof FirebaseError) {
        const errorCode = error.code;
        const errorMessage = error.message.toLowerCase();
        
        // Permission denied - return empty array instead of throwing
        if (errorCode === 'permission-denied' || errorMessage.includes('permission')) {
          console.warn('[CatchErrorService] Permission denied for collection read:', errorMessage);
          return of([]);
        }
        
        // Network/connection errors - return empty array for graceful degradation
        if (errorMessage.includes('offline') || errorMessage.includes('network') || 
            errorCode === 'unavailable' || errorCode === 'failed-precondition') {
          console.warn('[CatchErrorService] Network error for collection read:', errorMessage);
          return of([]);
        }
      }
      
      // Re-throw unexpected errors for Sentry reporting
      console.error('[CatchErrorService] Unexpected error in collection read:', error);
      return throwError(() => error);
    });
  }

  catchOfflineError = catchError((error) => {
    // Check if this is a network/connectivity related error
    const isNetworkError = error instanceof FirebaseError && (
      error.message.toLowerCase().includes("offline") ||
        error.message.toLowerCase().includes("network") ||
        error.message.toLowerCase().includes("transport") ||
        error.message.toLowerCase().includes("connection") ||
        error.message.toLowerCase().includes("unavailable") ||
      error.code === 'unavailable' ||
      error.code === 'failed-precondition'
    );

    // Also check for HTTP errors that indicate connectivity issues
    const isHttpError = error && (
      error.toString().includes("500") ||
      error.toString().includes("503") ||
      error.toString().includes("504")
    );

    // Permission-denied must emit a stream value, never rethrow into catchError((err) => err)
    // chains that pass Error objects to innerFrom (LOTTOSOCIAL-PWA-1M60).
    const isPermissionError =
      (error instanceof FirebaseError && error.code === 'permission-denied') ||
      (typeof error?.message === 'string' &&
        error.message.toLowerCase().includes('missing or insufficient permissions'));

    if (isPermissionError) {
      this.logError("Firestore permission-denied (returning null placeholder):", error);
      return of(null);
    }

    if (isNetworkError || isHttpError) {
      this.logError("Error setting up game iframe URL:", error);
      return of({
        data: () => ({}),
        exists: () => false,
        id: 'offline-placeholder',
        offline: true,    // Add flag to indicate offline status
        isOfflinePlaceholder: true
      });
    }
    return throwError(() => error);
  });

  readonly retryWhen: MonoTypeOperatorFunction<unknown> = retryWhen(
    (errors: Observable<Error>) =>
    errors.pipe(
      scan((errorCount: number, error: Error) => {
        if (error instanceof FirebaseError) {
          // Don't retry permission-denied errors as they won't succeed
            if (error.code === 'permission-denied') {
            return { shouldThrow: true, error };
          }

          const errorMessage = error.message.toLowerCase();
          const shouldRetry =
            errorMessage.includes(CommonVariable.offline) ||
            errorMessage.includes(CommonVariable.timeout) ||
            errorMessage.includes("websocket") ||
            errorMessage.includes("1006") ||
            errorMessage.includes("disconnected") ||
            errorMessage.includes("network error") ||
            errorMessage.includes("transport unavailable") ||
            (error.code && error.code.startsWith("unavailable"));

          if (shouldRetry) {
            if (errorCount >= this.errorCountMax) {
              return { shouldThrow: true, error };
            }
            return errorCount + 1;
          }
          return { shouldThrow: true, error };
        }
        return { shouldThrow: true, error };
      }, 0),
      mergeMap((result: number | { shouldThrow: boolean; error: Error }) => {
        if (typeof result !== "number" && result?.shouldThrow) {
          return throwError(() => result?.error);
        }
        return of(null);
      }),
        delay(this.delayTime)
      )
  );

  handleFirestoreError(error: unknown): Error {
    if (error instanceof FirebaseError) {
      return error;
    }
    return new Error('Unknown Firestore error');
  }
  logError(context: string, error: unknown): void {
    if (!environment.production) {
      console.error(context, error);
    }
    // Forward to error tracker as appropriate
  }
}
